import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumBillingPlanStatus } from "./enums/enumBillingPlanStatus.generated";

// Model Objects

export class BillingConfiguration extends ModelObject<BillingConfiguration> {

    public stripePublicApiKey: string = "";
    public productPerUserNetPrice: number = 0;
    public billingCurrency: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stripePublicApiKey = jsonSerializedObject.stripePublicApiKey;
          this.productPerUserNetPrice = jsonSerializedObject.productPerUserNetPrice;
          this.billingCurrency = jsonSerializedObject.billingCurrency;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stripePublicApiKey = this.stripePublicApiKey;
        toRet.productPerUserNetPrice = this.productPerUserNetPrice;
        toRet.billingCurrency = this.billingCurrency;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class BillingStatus extends ModelObject<BillingStatus> {

    public planStatus: EnumBillingPlanStatus = EnumBillingPlanStatus.trialling;
    public requiresNewSubscription: boolean = false;
    public permittedConcurrentUsers: number = 0;
    private _freeTrialExpirationDate: LocalDate | null = null;
    public get freeTrialExpirationDate(): LocalDate | null { return this._freeTrialExpirationDate }
    public set freeTrialExpirationDate(value: LocalDate | null) {
      this._freeTrialExpirationDate = value === undefined ? null : value;
    }
    public currentPeriodStart: Instant = Instant.ofEpochMilli(0);
    public currentPeriodEnd: Instant = Instant.ofEpochMilli(0);
    public pastDueBillingRetryDays: number = 0;
    private _cancelledAt: Instant | null = null;
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    public hasPaymentMethod: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.planStatus = EnumBillingPlanStatus.getByValue(jsonSerializedObject.planStatus, this.planStatus);
          this.requiresNewSubscription = jsonSerializedObject.requiresNewSubscription;
          this.permittedConcurrentUsers = jsonSerializedObject.permittedConcurrentUsers;
          this.freeTrialExpirationDate = jsonSerializedObject.freeTrialExpirationDate == null ? null : LocalDate.parse(jsonSerializedObject.freeTrialExpirationDate);
          this.currentPeriodStart = Instant.parse(jsonSerializedObject.currentPeriodStart);
          this.currentPeriodEnd = Instant.parse(jsonSerializedObject.currentPeriodEnd);
          this.pastDueBillingRetryDays = jsonSerializedObject.pastDueBillingRetryDays;
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.hasPaymentMethod = jsonSerializedObject.hasPaymentMethod;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.planStatus = this.planStatus.value;
        toRet.requiresNewSubscription = this.requiresNewSubscription;
        toRet.permittedConcurrentUsers = this.permittedConcurrentUsers;
        toRet.freeTrialExpirationDate = this.freeTrialExpirationDate == null ? null : this.freeTrialExpirationDate.toString();
        toRet.currentPeriodStart = this.currentPeriodStart.toString();
        toRet.currentPeriodEnd = this.currentPeriodEnd.toString();
        toRet.pastDueBillingRetryDays = this.pastDueBillingRetryDays;
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.hasPaymentMethod = this.hasPaymentMethod;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "permittedConcurrentUsers", rule: (v: any) => v == null || v >= -2147483648 || "PermittedConcurrentUsers is not greater than minimum allowed value: -2147483648"},
            {field: "permittedConcurrentUsers", rule: (v: any) => v == null || v <= 2147483647 || "PermittedConcurrentUsers is above maximum allowed value: 2147483647"},
            {field: "pastDueBillingRetryDays", rule: (v: any) => v == null || v >= -2147483648 || "PastDueBillingRetryDays is not greater than minimum allowed value: -2147483648"},
            {field: "pastDueBillingRetryDays", rule: (v: any) => v == null || v <= 2147483647 || "PastDueBillingRetryDays is above maximum allowed value: 2147483647"},
    ];
}


export class BillingDetails extends ModelObject<BillingDetails> {

    public stripeSubscriptionId: string = "";
    public stripeCustomerId: string = "";
    public billingStatus: BillingStatus = new BillingStatus();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stripeSubscriptionId = jsonSerializedObject.stripeSubscriptionId;
          this.stripeCustomerId = jsonSerializedObject.stripeCustomerId;
          this.billingStatus = new BillingStatus(jsonSerializedObject.billingStatus);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stripeSubscriptionId = this.stripeSubscriptionId;
        toRet.stripeCustomerId = this.stripeCustomerId;
        toRet.billingStatus = this.billingStatus.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "billingStatus", rule: (v: any) => !!v || "billingStatus is required"},
            {field: "billingStatus", rule: (v: any) => v.validate()},
    ];
}


export class BillingPlanDb extends ModelObject<BillingPlanDb> {

    /**
    * The external stripe reference to the billing customer.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public stripeCustomerId: string = "";
    /**
    * The external stripe reference to the billing subscription.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public stripeSubscriptionId: string = "";
    /**
    * Number of concurrent users with access to the product (cached value from Stripe for offline fallback).
    */
    public permittedConcurrentUsers: number = 0;
    /**
    * The current status of the billing plan (cached value from Stripe).
    */
    public billingPlanStatus: EnumBillingPlanStatus = EnumBillingPlanStatus.trialling;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stripeCustomerId = jsonSerializedObject.stripeCustomerId;
          this.stripeSubscriptionId = jsonSerializedObject.stripeSubscriptionId;
          this.permittedConcurrentUsers = jsonSerializedObject.permittedConcurrentUsers;
          this.billingPlanStatus = EnumBillingPlanStatus.getByValue(jsonSerializedObject.billingPlanStatus, this.billingPlanStatus);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stripeCustomerId = this.stripeCustomerId;
        toRet.stripeSubscriptionId = this.stripeSubscriptionId;
        toRet.permittedConcurrentUsers = this.permittedConcurrentUsers;
        toRet.billingPlanStatus = this.billingPlanStatus.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "permittedConcurrentUsers", rule: (v: any) => v == null || v >= -2147483648 || "PermittedConcurrentUsers is not greater than minimum allowed value: -2147483648"},
            {field: "permittedConcurrentUsers", rule: (v: any) => v == null || v <= 2147483647 || "PermittedConcurrentUsers is above maximum allowed value: 2147483647"},
    ];
}


export class BillingPlanService extends ServiceBase {

  public async getBillingConfiguration(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BillingConfiguration> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = (response:any) => new BillingConfiguration(response.billingConfiguration);;
    return this.makeJsonRequest("billingPlan/v1/getBillingConfiguration", req, false, extractor, opts);
  };

  public async getBillingStatus(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BillingStatus> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = (response:any) => new BillingStatus(response.billingPlan);;
    return this.makeJsonRequest("billingPlan/v1/getBillingStatus", req, false, extractor, opts);
  };

  public async createNewSubscription(tenantGroupId: Guid, numberOfConcurrentUsers: number, returnUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    numberOfConcurrentUsers:numberOfConcurrentUsers,
    returnUrl:returnUrl,
    };
    const extractor = (response:any) => response.stripeCheckoutSession;;
    return this.makeJsonRequest("billingPlan/v1/createNewSubscription", req, false, extractor, opts);
  };

  public async getCustomerPortalUrl(tenantGroupId: Guid, returnUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    returnUrl:returnUrl,
    };
    const extractor = (response:any) => response.customerPortalRedirectUrl;;
    return this.makeJsonRequest("billingPlan/v1/getCustomerPortalUrl", req, false, extractor, opts);
  };

  public async capturePaymentMethod(tenantGroupId: Guid, successReturnUrl: string, failureReturnUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    successReturnUrl:successReturnUrl,
    failureReturnUrl:failureReturnUrl,
    };
    const extractor = (response:any) => response.stripeCheckoutSession;;
    return this.makeJsonRequest("billingPlan/v1/capturePaymentMethod", req, false, extractor, opts);
  };

  public async assignCapturedPaymentMethodToSubscription(tenantGroupId: Guid, stripeCheckoutSession: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    stripeCheckoutSession:stripeCheckoutSession,
    };
    const extractor = null;
    return this.makeJsonRequest("billingPlan/v1/assignCapturedPaymentMethodToSubscription", req, false, extractor, opts);
  };

  public async getBillingDetails(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BillingDetails | null> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = (response:any) => response.billingDetails == null ? null : new BillingDetails(response.billingDetails);;
    return this.makeJsonRequest("billingPlan/v1/getBillingDetails", req, false, extractor, opts);
  };

  public async getXeroSynchronisationLoginUrl(callbackUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    callbackUrl:callbackUrl,
    };
    const extractor = (response:any) => response.url;;
    return this.makeJsonRequest("billingPlan/v1/getXeroSynchronisationLoginUrl", req, false, extractor, opts);
  };

  public async synchroniseWithXero(startedSinceInclusive: LocalDate, temporaryCode: string, state: string, callbackUri: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    startedSinceInclusive:startedSinceInclusive.toString(),
    temporaryCode:temporaryCode,
    state:state,
    callbackUri:callbackUri,
    };
    const extractor = null;
    return this.makeJsonRequest("billingPlan/v1/synchroniseWithXero", req, false, extractor, opts);
  };

  public async updateFreeTrialEndInstant(tenantGroupId: Guid, newFreeTrialEndInstant: Instant, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BillingDetails> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    newFreeTrialEndInstant:newFreeTrialEndInstant.toString(),
    };
    const extractor = (response:any) => new BillingDetails(response.billingDetails);;
    return this.makeJsonRequest("billingPlan/v1/updateFreeTrialEndInstant", req, false, extractor, opts);
  };

  public async updateBrewmanSubscriptionUsers(tenantGroupId: Guid, quantity: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    quantity:quantity,
    };
    const extractor = null;
    return this.makeJsonRequest("billingPlan/v1/updateBrewmanSubscriptionUsers", req, false, extractor, opts);
  };

  public async createLabelOrderStripeCheckoutSession(tenantGroupId: Guid, successReturnUrl: string, failureReturnUrl: string, labelOrderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    successReturnUrl:successReturnUrl,
    failureReturnUrl:failureReturnUrl,
    labelOrderId:labelOrderId.toString(),
    };
    const extractor = (response:any) => response.stripeCheckoutSession;;
    return this.makeJsonRequest("billingPlan/v1/createLabelOrderStripeCheckoutSession", req, false, extractor, opts);
  };

  public async synchroniseLabelOrdersWithXero(startedSinceInclusive: LocalDate, temporaryCode: string, state: string, callbackUri: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    startedSinceInclusive:startedSinceInclusive.toString(),
    temporaryCode:temporaryCode,
    state:state,
    callbackUri:callbackUri,
    };
    const extractor = null;
    return this.makeJsonRequest("billingPlan/v1/synchroniseLabelOrdersWithXero", req, false, extractor, opts);
  };

  public async createStripeTradePortalTransactionFeeRecord(orderTotalNet: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderTotalNet:orderTotalNet,
    };
    const extractor = null;
    return this.makeJsonRequest("billingPlan/v1/createStripeTradePortalTransactionFeeRecord", req, false, extractor, opts);
  };

  public async getBillingPlan(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BillingPlanDb | null> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = (response:any) => response.billingPlan == null ? null : new BillingPlanDb(response.billingPlan);;
    return this.makeJsonRequest("billingPlan/v1/getBillingPlan", req, false, extractor, opts);
  };

  public async getStripePublicApiKey(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    };
    const extractor = (response:any) => response.stripePublicApiKey;;
    return this.makeJsonRequest("billingPlan/v1/getStripePublicApiKey", req, false, extractor, opts);
  };

  public async createLabelOrderStripeCheckoutSessionUnauthenticated(successReturnUrl: string, failureReturnUrl: string, labelOrderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    successReturnUrl:successReturnUrl,
    failureReturnUrl:failureReturnUrl,
    labelOrderId:labelOrderId.toString(),
    };
    const extractor = (response:any) => response.stripeCheckoutSession;;
    return this.makeJsonRequest("billingPlan/v1/createLabelOrderStripeCheckoutSessionUnauthenticated", req, false, extractor, opts);
  };
}

export const billingPlanService = new BillingPlanService();